import { ClipboardCheckIcon } from 'lucide-react'
import React, { useCallback, useState } from 'react'

import { useListProjects } from '@/api/assessments.hook'
import { CompanyStatus } from '@/gen/inventory/v1/company_status_pb'
import { Project } from '@/gen/inventory/v1/project_pb'

import { useInherentRiskItems } from '@/lib/risk/inherent-risk-items.hook'
import { UrlParam } from '@/lib/url/url-param.hook'
import { buildThirdPartyUrl } from '@/lib/url/urls'

import { useCompanyHasAssessments } from '@/pages/assessment/ag-grid-assessment/config/use-company-has-assessments'
import { PreAssessmentStep } from '@/pages/assessment/pre-assessment-step-enum'
import { CompanyDrawerTabs } from '@/pages/company-drawer/company-drawer-tabs-enum'
import { ActionItem } from '@/pages/company-drawer/company-overview/action-item/action-item'
import { SetIrqModal } from '@/pages/company-drawer/company-overview/set-irq-modal/set-irq-modal'
import { useUpcomingPreAssessmentStep } from '@/pages/company-drawer/use-upcoming-pre-assessment-step'

import { StringNameAvatar } from '@/components/lemmala-design-system/avatar'
import { TextWithIcon } from '@/components/lemmala-design-system/text-with-icon/text-with-icon'

type NextPreAssessmentActionButtonProps = {
  companyId: string
  status: CompanyStatus
  from: string
  engagementModeEnabled: boolean
  project?: Project
  actionButton?: (props: { actionText?: string; onClick: () => void }) => React.ReactNode
}

export const NextPreAssessmentActionButton = ({
  companyId,
  status,
  from,
  engagementModeEnabled,
  project,
  actionButton,
}: NextPreAssessmentActionButtonProps) => {
  const upcomingStep = useUpcomingPreAssessmentStep(companyId, engagementModeEnabled, status)
  const { projects } = useListProjects()
  const { hasOngoingAssessments } = useCompanyHasAssessments(companyId)
  const [isIrqModalOpen, setIsIrqModalOpen] = useState(false)
  const inherentRiskData = useInherentRiskItems(companyId)

  const navigateToAssessment = useCallback(
    (step: PreAssessmentStep) => {
      if (step === PreAssessmentStep.IRQ) {
        setIsIrqModalOpen(true)
      } else if (step === PreAssessmentStep.START) {
        window.open(
          buildThirdPartyUrl(companyId, {
            [UrlParam.COMPANY_DRAWER_TAB]: CompanyDrawerTabs.ASSESSMENT,
          }),
          '_blank',
        )
      }
    },
    [companyId],
  )

  if (!upcomingStep) {
    return null
  }

  const stepToText: Record<PreAssessmentStep, string> = {
    start: 'Start assessment',
    irq: 'Validate Inherent Risk',
  }

  return (
    <>
      {actionButton ? (
        actionButton({
          actionText: stepToText[upcomingStep],
          onClick: () => navigateToAssessment(upcomingStep),
        })
      ) : (
        <ActionItem
          text={
            <TextWithIcon
              text={stepToText[upcomingStep]}
              icon={
                project?.name && (
                  <StringNameAvatar name={project?.name} className='size-3.5 rounded text-xs' />
                )
              }
            />
          }
          icon={<ClipboardCheckIcon size={14} />}
          onClick={() => navigateToAssessment(upcomingStep)}
        />
      )}
      {isIrqModalOpen && (
        <SetIrqModal
          companyId={companyId}
          from={from}
          mode={projects.length === 0 && hasOngoingAssessments ? 'edit' : 'set'}
          engagementModeEnabled={engagementModeEnabled}
          inherentRiskData={inherentRiskData}
          onClose={() => setIsIrqModalOpen(false)}
        />
      )}
    </>
  )
}
